/**
 * NFC normalization util · P1.5.5
 *
 * Wraps `String.prototype.normalize('NFC')` with the application-level
 * conventions for ai-workflow:
 *
 *   - All TEXT written to D1 must pass through `normalizeNfc()`
 *   - All R2 keys + tree manifest paths must use `normalizePath()`
 *   - Use `nfcSchema` zod refinement at API entry boundaries
 *
 * Why: macOS HFS+/APFS uses NFD by default · Linux ext4 uses raw bytes ·
 * NFC vs NFD bytes differ → "北京" stored as 6 bytes (NFC) vs 12 bytes (NFD)
 * causes false BINARY mismatch + double-counting in FTS index. We pin
 * everything to NFC at write time.
 *
 * See:
 *   - architecture.md §17 A26 (中文规范)
 *   - tech-stack.md §11.0
 *   - protocol.md §15
 *   - P0.1 spike s4-r2-e2e (NFC + cross-platform path test)
 */

import { z } from 'zod';

/**
 * Normalize a string to Unicode NFC form.
 *
 * Idempotent: `normalizeNfc(normalizeNfc(s)) === normalizeNfc(s)`.
 *
 * @example
 * ```ts
 * const nfd = '北京';  // assume NFD: U+5317 then U+4EAC combiners
 * const nfc = normalizeNfc(nfd);  // U+5317 + U+4EAC composed
 * nfc === '北京'  // true
 * ```
 */
export function normalizeNfc(s: string): string {
  return s.normalize('NFC');
}

/**
 * Normalize a path string by splitting on `/` and NFC-normalizing each segment.
 * The separator `/` is preserved exactly (not run through NFC).
 *
 * Why segment-wise: prevents accidental NFC-normalization of `/` itself
 * (it's already ASCII, but defensive). Also prevents Unicode separators
 * like `／` (FF0F · fullwidth solidus) from being misinterpreted as path
 * separators after composition.
 *
 * Empty segments (leading/trailing `/`) are preserved.
 *
 * @example
 * ```ts
 * normalizePath('/北京/报告.md')  // '/北京/报告.md' all NFC
 * normalizePath('a/b//c')  // 'a/b//c' (double slash preserved)
 * normalizePath('')  // ''
 * ```
 */
export function normalizePath(path: string): string {
  if (path === '') return '';
  return path.split('/').map(normalizeNfc).join('/');
}

/**
 * Predicate: returns true iff the string equals its NFC form.
 *
 * Use this for `z.string().refine(isNfc)` in API input validation.
 * If the string is already NFC, returns true. If it would change after
 * normalization, returns false (caller should normalize before storing).
 *
 * @example
 * ```ts
 * isNfc('北京')  // true (already NFC)
 * isNfc('北̏')  // false (combining char form, would normalize)
 * ```
 */
export function isNfc(s: string): boolean {
  return s === s.normalize('NFC');
}

/**
 * Zod schema: validates that a string is in NFC form.
 *
 * Use at API entry points to reject non-normalized input rather than
 * silently normalizing (which can hide client bugs).
 *
 * @example
 * ```ts
 * const NodeName = nfcString.min(1).max(255);
 * NodeName.parse('北京')  // OK
 * NodeName.parse('北̏')  // throws ZodError
 * ```
 */
export const nfcString = z.string().refine(isNfc, {
  message: 'String must be in Unicode NFC form. Call normalizeNfc() before storing.',
});

/**
 * Zod schema for path-like strings: each `/`-separated segment must be NFC.
 *
 * @example
 * ```ts
 * const R2Key = nfcPath.min(1).max(1024);
 * R2Key.parse('北京/报告.md')  // OK
 * ```
 */
export const nfcPath = z.string().refine(
  (p) => p === normalizePath(p),
  { message: 'Path must have all segments in NFC form. Call normalizePath() before using.' },
);

/**
 * Combined assertion: throws if string isn't NFC. Useful in service-layer
 * code where you want a hard failure rather than a refinement error path.
 */
export function assertNfc(s: string, label = 'string'): void {
  if (!isNfc(s)) {
    throw new Error(`${label} is not in NFC form (would normalize on application). Call normalizeNfc() first.`);
  }
}
